import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La sessione viaggia in un cookie httpOnly impostato dal server: va inviato con ogni richiesta
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Nessun token in localStorage: 'token' indica solo se la sessione è attiva (compatibilità)
  const token = user ? 'session' : null;
  const [loading, setLoading] = useState(true);
  
  // Impersonate: Admin visualizza come un Capogruppo (persiste in sessionStorage)
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    const saved = sessionStorage.getItem('impersonatedUser');
    return saved ? JSON.parse(saved) : null;
  });

  // Convenzione attiva: Super Admin/Admin/Top Agent "entra" in una convenzione
  // per filtrare Rubrica/Offerte/Ordini (persistente in sessionStorage)
  const [activeConvenzione, setActiveConvenzione] = useState(() => {
    const saved = sessionStorage.getItem('activeConvenzione');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const initAuth = async () => {
      // Rimuove eventuali token legacy salvati prima del passaggio ai cookie
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    
    // Se la registrazione è in pending, NON fare login
    if (response.data.pending) {
      return { 
        pending: true, 
        message: response.data.message 
      };
    }
    
    // Solo per admin (primo utente)
    if (response.data.access_token) {
      const { user: userData } = response.data;
      setUser(userData);
      return userData;
    }
    
    return response.data;
  };

  const logout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    // Clear session storage (catalog filters, scroll position, impersonation, etc.)
    sessionStorage.clear();
    setUser(null);
    setImpersonatedUser(null);  // Reset impersonation on logout
  };

  // Impersonate: Admin/Super Admin visualizza come un utente specifico (Capogruppo o Top Agent)
  const startImpersonate = (targetUser) => {
    if (!['admin', 'super_admin'].includes(user?.role)) return;  // Solo Admin/Super Admin può impersonare
    setImpersonatedUser(targetUser);
    sessionStorage.setItem('impersonatedUser', JSON.stringify(targetUser));
  };

  const stopImpersonate = () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem('impersonatedUser');
  };
  
  // Convenzione attiva: filtra Rubrica/Offerte/Ordini
  const enterConvenzione = (convenzione) => {
    if (!convenzione) return;
    setActiveConvenzione(convenzione);
    sessionStorage.setItem('activeConvenzione', JSON.stringify(convenzione));
  };
  const exitConvenzione = () => {
    setActiveConvenzione(null);
    sessionStorage.removeItem('activeConvenzione');
  };

  // Auto-login dopo registrazione pubblica: il cookie è già impostato dal server
  const setAuth = (_accessToken, userData) => {
    setUser(userData);
  };

  // Il cookie httpOnly sostituisce l'header Authorization (funzione mantenuta per compatibilità)
  const getAuthHeader = useCallback(() => ({}), []);

  // Ritorna i parametri da aggiungere alle chiamate API quando si sta impersonando
  const getImpersonateParams = () => {
    // Leggi direttamente da sessionStorage per evitare problemi di timing
    const savedImpersonate = sessionStorage.getItem('impersonatedUser');
    const params = {};
    if (savedImpersonate) {
      try {
        const parsed = JSON.parse(savedImpersonate);
        if (parsed?.id) params.view_as = parsed.id;
      } catch (e) {
        console.error('Error parsing impersonatedUser:', e);
      }
    }
    const savedConvenzione = sessionStorage.getItem('activeConvenzione');
    if (savedConvenzione) {
      try {
        const parsed = JSON.parse(savedConvenzione);
        if (parsed?.id) params.convenzione_id = parsed.id;
      } catch (e) {
        console.error('Error parsing activeConvenzione:', e);
      }
    }
    return params;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      setAuth, 
      getAuthHeader,
      // Impersonate
      impersonatedUser,
      startImpersonate,
      stopImpersonate,
      getImpersonateParams,
      // Convenzione attiva
      activeConvenzione,
      enterConvenzione,
      exitConvenzione
    }}>
      {children}
    </AuthContext.Provider>
  );
};
